import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ContentChild,
	forwardRef,
	HostBinding,
	inject,
	Input,
	OnChanges,
	OnInit,
	SimpleChanges,
	TemplateRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import deepEqual from "fast-deep-equal";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { map } from "rxjs/operators";

import { SelectorItem } from "../interfaces";

const EMPTY_SET = new Set<unknown>(["", undefined, null]);

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => SelectorComponent),
	multi: true,
};

@Component({
	selector: "ic-selector",
	styleUrls: ["selector.component.scss"],
	providers: [INPUT_CONTROL_ACCESSOR],
	templateUrl: "./selector.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class SelectorComponent implements ControlValueAccessor, OnChanges, OnInit {
	private readonly cdRef = inject(ChangeDetectorRef);

	/** Sets the error class of the component. */
	@HostBinding("class.error") @Input() error = false;

	/** Optional label to be shown above the input. */
	@Input() label?: string;
	/** Optional help text to be shown alongside the label. */
	@Input() help?: string;
	/** Boolean to determine whether or not "Optional" tag should be shown in the label. */
	@Input() required = true;
	/** Boolean to determine whether or not multiple items can be selected. */
	@Input() multi = false;
	/** Boolean to determine whether or not the item is input is unsettable or not. */
	@Input() isUnsettable = true;
	/** Type to determine the size of the input buttons. */
	@Input() size: "large" | "medium" | "small" | "xlarge" = "large";
	/** Optional compare function to determine which item is selected. */
	@Input() compare?: (item: unknown, selected: unknown) => boolean;
	/** List of items to be displayed. */
	@Input() items: unknown[] = [];
	/** Optional key of the item we want to have as our formValue (i.e 'name' of {id: 1, name: 'Hello'}). */
	@Input() key?: string;

	/** Template for item option. */
	@ContentChild("item", { static: false }) itemTemplate: TemplateRef<unknown>;

	protected disabled = false;
	public selectorItems$: Observable<SelectorItem[]>;
	private selectedItems$ = new BehaviorSubject<unknown[]>([]);

	private items$ = new BehaviorSubject<unknown[]>([]);
	private onTouch = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onChange = (_: unknown) => {};

	/**
	 * Set the selectedItems$ as a result of a patchValue/setValue.
	 *
	 * @param - Value Value set to the AbstractControl.
	 */
	writeValue(value: unknown | unknown[]): void {
		if (value === undefined || value === null || value === "") {
			this.selectedItems$.next([]);

			this.cdRef.markForCheck();

			return;
		}

		this.selectedItems$.next(Array.isArray(value) ? value : [value]);

		this.cdRef.markForCheck();
	}

	/** Register onChange function of ControlValueAccessor. */
	registerOnChange(fn: (value: unknown) => void): void {
		this.onChange = fn;
	}

	/** Register onTouch function of ControlValueAccessor. */
	registerOnTouched(fn: () => void): void {
		this.onTouch = fn;
	}

	/**
	 * Set the disable state of control.
	 *
	 * @param isDisabled Disabled state of control.
	 */
	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;

		this.cdRef.markForCheck();
	}

	/** Toggle a value from the selectedItems$ array. */
	protected selectItem(selectedItem: SelectorItem): void {
		let selectedItems = [...this.selectedItems$.getValue()];
		const { selected, item } = selectedItem;
		// If a key was provided, we need get the value that was selected
		const selectedValue = this.key ? item[this.key] : item;

		// If the item has already been selected, we delete it from the selected items
		if (selected) {
			const index = selectedItems.findIndex((i) => this.compareItems(item, i));
			selectedItems = [...selectedItems.slice(0, index), ...selectedItems.slice(index + 1)];
		}
		// If the input allows us to select multiple items, we add it to the selected items, if not we make a new Set
		else if (this.multi) {
			selectedItems.push(selectedValue);
		} else {
			selectedItems = [selectedValue];
		}

		this.selectedItems$.next(selectedItems);

		// Send change of value to control
		this.onChange(this.multi ? selectedItems : selectedItems[0]);
		this.markAsTouched();

		this.cdRef.markForCheck();
	}

	/** Mark the control as touched. */
	protected markAsTouched(): void {
		this.onTouch();
	}

	/**
	 * Determine whether an item is selected by matching it with the selectedItems.
	 *
	 * @param item - Item from the inputted items.
	 * @param selectedItems - List of selected items.
	 */
	private isSelected(item: unknown, selectedItems: unknown[]): SelectorItem {
		// Check if the item exists in the selectedItems
		const itemInSelectedItems = selectedItems.find((selectedItem) => this.compareItems(item, selectedItem));

		return {
			item,
			generatedIdentifier:
				"option-"
				+ (this.key
					? item[this.key] === Object(item[this.key])
						? Object.values(item[this.key]).join("-")
						: item[this.key]
					: item === Object(item)
						? Object.values(item).join("-")
						: item),
			selected: !EMPTY_SET.has(itemInSelectedItems),
		};
	}

	/**
	 * Determine whether two items match.
	 *
	 * @param item - Item from the inputted items.
	 * @param selectedItem - Item from the inputted items or key value of an item from the inputted items.
	 */
	private compareItems(item: unknown, selectedItem: unknown): boolean {
		// If a compare function was provided, we use this function
		if (this.compare) {
			return this.compare(item, selectedItem);
		}

		// If a key was provided, we compare based on said key
		if (this.key) {
			// The selectedItem in this case is a property matching the key
			return deepEqual(item[this.key], selectedItem);
		}

		return deepEqual(item, selectedItem);
	}

	/** Set up observable to map the inputted items to an array of `SelectorItems` to determine which are selected. */
	private determineSelectedItems(): void {
		this.selectorItems$ = combineLatest([this.items$, this.selectedItems$]).pipe(
			map(([items, selectedItems]) => {
				// If the items do not exist or there are no selected items yet, we do an early exit.
				if (!items || items.length === 0) {
					return [];
				}

				return items.map((item) => this.isSelected(item, selectedItems));
			}),
		);
	}

	ngOnInit(): void {
		this.determineSelectedItems();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.items) {
			this.items$.next(this.items);
		}
	}
}
